/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useMemo } from 'react';
import { css, StyleSheet } from 'aphrodite';
import { accessibilityUtils, mixins } from '@blend/consumer-components';
import { useConsumerTheme } from '../../hooks/useConsumerTheme';

export namespace LinkButtonWrapper {
  export interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    // Control the color. If unset, the link will default to the action color.
    color?: string;
    // Control the width of the button. If unset, the link will default to full.
    sizing?: 'auto' | 'full';
    // Create a span that looks like a button, which can be overlayed over file inputs in an accessible way.
    fake?: boolean;
    onClick?: () => void;
  }
}

export const LinkButtonWrapper: React.FC<LinkButtonWrapper.Props> = ({
  type = 'button',
  color,
  sizing = 'full',
  fake,
  children,
  onClick,
  ...htmlProps
}) => {
  const {
    appConfig: { colors },
  } = useConsumerTheme();
  const styles = useMemo(
    () =>
      StyleSheet.create({
        linkButtonWrapper: {
          padding: 0,
          width: sizing === 'full' ? '100%' : 'auto',
          border: 'none',
          background: 'none',
          boxShadow: 'none',
          textAlign: 'left',
          color: color || colors.action,
          ':hover': {
            color: mixins.darken(color || colors.action, 0.15),
          },
        },
        fake: {
          cursor: 'pointer',
        },
      }),
    [],
  );

  if (fake) {
    return (
      <span
        {...htmlProps}
        tabIndex={0}
        onClick={onClick}
        onKeyDown={onClick && accessibilityUtils.generateOnKeyUp(onClick)}
        className={css(styles.linkButtonWrapper, styles.fake)}
      >
        {children}
      </span>
    );
  }

  return (
    <button {...htmlProps} onClick={onClick} type={type} className={css(styles.linkButtonWrapper)}>
      {children}
    </button>
  );
};
